import { createTheme } from "@mui/material/styles";

import { getOrigin } from "./utilities";

/** @todo finalize poll ids, labels, and sectionColors */
// object to get label and sectionColor from slide id
export const SLIDE_ADDITIONAL_INFO = Object.freeze({
  title: { sectionColor: "#EB564B" },
  warmup: { label: "Warm Up" },
  lastepisode: { label: "Last Episode", sectionColor: "#FF9166" },
  getready: { label: "Get Ready", sectionColor: "#FFE478" },
  explore: { label: "Explore", sectionColor: "#9FCE3A" },
  learn: { label: "Learn", sectionColor: "#0089FF" },
  keepgoing: { label: "Keep Going", sectionColor: "#D89EFF" },
  checkin: { label: "Check In", sectionColor: "#6841C0" },
  playtime: { label: "Play!", nextLabel: "Start Timer", notch: true },
  pollpreview: { label: "Poll Preview", notch: true },
  polltime: { label: "Poll Time!" },
  pollresults: { label: "Poll Results" },
  letsdiscuss: { label: "Let's Discuss", notch: true },
  discusspartner: { label: "Discuss with a partner", notch: true },
  end: { label: "End" },
});

export const PLAYER_STATUS = {
  PAUSED: "paused",
  POLLING: "polling",
  PLAYING: "playing",
};

export const PLAYTIME_PHASE = {
  LOGIN: "login",
  EXPLORE: "explore",
  KEEPGOING: "keepgoing",
  FREEPLAY: "freeplay",
};

export const DISCUSS_PARTNER = "discusspartner";

export const TIMER_MODAL_SUBPHASE = {
  START: "start",
  END: "end",
  END_EARLY: "endEarly",
};

export const EDITING_STATE = {
  IS_EDITING: "IS_EDITING",
  CANCEL_EDIT_QUEUED: "CANCEL_EDIT_QUEUED",
  CONFIRM_EDIT_QUEUED: "CONFIRM_EDIT_QUEUED",
};

export const PALETTE = {
  black: "#0b0f0b",
  secondaryBlack: "#181d18",
  overlayBlack: "#212121",
  green: "#00E676",
  contrastGreen: "#00A152",
  darkGreen: "#003918",
  desaturateGreen: "#1CB35E",
  gray: "#BDBDBD",
  lightGray: "#d9d9d9",
  underlineGray: "#9e9e9e",
};

export const getColorWithAlpha = (hexColor, opacityDecimal) =>
  `${hexColor}${Math.round(opacityDecimal * 255).toString(16)}`;

export const BUTTON_THEME = createTheme({
  palette: {
    primary: {
      main: PALETTE.green,
      contrastText: PALETTE.darkGreen,
      dark: PALETTE.desaturateGreen,
    },
    secondary: {
      main: PALETTE.lightGray,
      contrastText: PALETTE.secondaryBlack,
      dark: PALETTE.gray,
    },
  },
});

const NAV_KEYS = {
  prev: {
    PageUp: true,
    ArrowUp: true,
    ArrowLeft: true,
  },
  next: {
    PageDown: true,
    ArrowDown: true,
    ArrowRight: true,
  },
};
export const isNavKey = (type, key) => !!NAV_KEYS[type][key];
export const DIRECTION = {
  NEXT: "next",
  PREV: "prev",
};

export const BLOCK_KEYS = Object.freeze({
  and: "and",
  boom: "boom",
  defeat: "defeat",
  godown: "godown",
  goleft: "goleft",
  goright: "goright",
  goup: "goup",
  hot: "hot",
  is: "is",
  melt: "melt",
  not: "not",
  open: "open",
  push: "push",
  safe: "safe",
  shut: "shut",
  sink: "sink",
  stop: "stop",
  weak: "weak",
  win: "win",
  you: "you",
});

export const OBJECT_KEYS = Object.freeze({
  apple: "apple",
  ball: "ball",
  book: "book",
  bookshelf: "bookshelf",
  box: "box",
  bubble: "bubble",
  carrots: "carrots",
  castle: "castle",
  chicken: "chicken",
  clam: "clam",
  coin: "coin",
  crabCowboy: "crabCowboy",
  dresserFloral: "dresserFloral",
  fire: "fire",
  flower: "flower",
  fly: "fly",
  fossil: "fossil",
  frog: "frog",
  fungus: "fungus",
  gold: "gold",
  grass: "grass",
  hay: "hay",
  heart: "heart",
  hedgeFarm: "hedgeFarm",
  hedgeMaze: "hedgeMaze",
  ice: "ice",
  lava: "lava",
  lavaPit: "lavaPit",
  lavaUnderwater: "lavaUnderwater",
  moat: "moat",
  mushroom: "mushroom",
  puddle: "puddle",
  rocksAlgae: "rocksAlgae",
  rocksBlue: "rocksBlue",
  rocksGrey: "rocksGrey",
  rocksSnow: "rocksSnow",
  rocksTidepool: "rocksTidepool",
  rugPeach: "rugPeach",
  rugPink: "rugPink",
  rugTeal: "rugTeal",
  sandcastle: "sandcastle",
  seaweed: "seaweed",
  shrub: "shrub",
  snow: "snow",
  snowball: "snowball",
  snowbear: "snowbear",
  snowbunny: "snowbunny",
  soilSprout: "soilSprout",
  spike: "spike",
  starfish: "starfish",
  sword: "sword",
  wallPlatformGrey: "wallPlatformGrey",
  wallPlatformOrange: "wallPlatformOrange",
  wallPlatformPlantsGrey: "wallPlatformPlantsGrey",
  wallPlatformPlantsOrange: "wallPlatformPlantsOrange",
  water: "water",
  watermelon: "watermelon",
});

export const CHARACTER_KEYS = Object.freeze({
  enoki: "enoki",
  lino: "lino",
  lopi: "lopi",
  luna: "luna",
  mentiMasked: "mentiMasked",
  mentiUnmasked: "mentiUnmasked",
  morel: "morel",
  olive: "olive",
  oliveBowl: "oliveBowl",
  piney: "piney",
  sol: "sol",
  zorro: "zorro",
});

export const SLIDES_BASE_URL = "https://9dots.slides.com";

export const TRUSTED_MESSAGE_ORIGINS = Object.freeze({
  [SLIDES_BASE_URL]: true,
  // platform
  "http://localhost:3000": true,
  "https://school-qa-dfa0a.firebaseapp.com": true,
  "https://app.9dots.org": true,
  // play
  [getOrigin("play")]: true,
});

// INTERNAL GAMEPLAY IFRAME / PLATFORM WINDOW MESSAGING

export const INTERNAL_POST_MESSAGE_EVENT_NAMES = Object.freeze({
  // receives from the platform window
  FREEPLAY_EVENT: "FREEPLAY",
  // receives from platform window and forwards data to gameplay iframe
  LOGIN_EVENT: "LOGIN",
  // sent to gameplay iframe if not embedded
  SKIP_LOGIN: "SKIP_LOGIN",
  // sends to platform window and also receives from gameplay iframe
  MESSAGING_READY: "MESSAGING_READY",
  // can be received by platform window and can be sent to gameplay iframe
  INIT_CTRL_INPUT_TYPE_DISPATCHING_EVENT: "INIT_CTRL_INPUT_TYPE_DISPATCHING",
  // can be sent to platform window and can be received by gameplay iframe
  CTRL_INPUT_TYPE_DISPATCH_EVENT: "CTRL_INPUT_TYPE_DISPATCH",
});
